import React, { useEffect, useRef, useState } from "react";
import { Flex, type UploadFile, type UploadProps } from "antd";
import ImgCrop from "antd-img-crop";
import { ImageInputFieldProps } from "./interfaces";
import {
  ImageInpupButton,
  ImageInputColumn,
  ImageInputWrapper,
  StyledInputLabel,
  StyledUpload,
  StyledVerticalSpace,
} from "./style";
import { ReactComponent as PencilIcon } from "../../assets/icons/PencilSmall.svg";
import { ReactComponent as CrossCircleIcon } from "../../assets/icons/CrossCircleSmall.svg";
import { ReactComponent as PortraitIcon } from "../../assets/icons/Portrait.svg";
import { useTranslation } from "react-i18next";

export const ImageInputField = (props: ImageInputFieldProps) => {
  const {
    value,
    onChange,
    isEditable,
    label,
    type,
    isLargeVideoStyle,
    aspect,
  } = props;
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const draggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      setFileList([
        {
          url: value,
        } as UploadFile,
      ]);
    } else {
      setFileList([]);
    }
  }, [value]);

  useEffect(() => {
    let objectUrl: string | undefined;

    if (fileList[0]?.originFileObj) {
      objectUrl = URL.createObjectURL(fileList[0].originFileObj);
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileList]);

  const handleUploadChange: UploadProps["onChange"] = (info: any) => {
    const file = info.file;
    setFileList([file]);

    const reader = new FileReader();
    reader.onload = () => {
      const binaryFile = new Blob([reader.result as ArrayBuffer], {
        type: file.type,
      });
      onChange &&
        onChange({
          fileName: file.name,
          fileType: file.type,
          binaryFile: binaryFile,
        });
    };
    if (file.originFileObj) {
      reader.readAsArrayBuffer(file.originFileObj);
    }
  };

  const handleRemove = (file: any) => {
    setFileList([]);
    onChange &&
      onChange({
        fileName: "",
        fileType: "",
        binaryFile: null,
      });
  };

  const handleChangeBtn = () => {
    if (draggerRef.current) {
      const fileInput = draggerRef.current.querySelector(
        'input[type="file"]'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }
  };

  const uploadProps: UploadProps = {
    name: "file",
    fileList: fileList,
    multiple: false,
    onChange: handleUploadChange,
    customRequest: ({ onSuccess }) => {
      onSuccess && onSuccess("ok");
    },
  };

  return (
    <StyledVerticalSpace isLargeVideoStyle={isLargeVideoStyle}>
      {label ? <StyledInputLabel>{label}</StyledInputLabel> : null}
      <ImageInputWrapper isLargeVideoStyle={isLargeVideoStyle}>
        <ImageInputColumn ref={draggerRef}>
          {type === "video" ? (
            <StyledUpload
              {...uploadProps}
              accept={".mp4"}
              isEditable={!isEditable && fileList.length === 0 ? false : true}
              itemRender={(originNode, file) => {
                if (type === "video" && fileList.length !== 0) {
                  return (
                    <Flex>
                      <video width="348" height="180" controls={true}>
                        <source
                          src={
                            file.url ||
                            (file.originFileObj &&
                              URL.createObjectURL(file.originFileObj))
                          }
                        />
                      </video>
                    </Flex>
                  );
                }
                return originNode;
              }}
            >
              {fileList.length === 0 && !isEditable ? (
                <p className="ant-upload-drag-icon">
                  <PortraitIcon />
                </p>
              ) : fileList.length === 0 && isEditable ? (
                "+ " + t("Upload Video")
              ) : null}
            </StyledUpload>
          ) : (
            <ImgCrop
              aspect={aspect}
              rotationSlider
              fillColor="rgba(0, 0, 0, 0)"
            >
              <StyledUpload
                {...uploadProps}
                accept={".jpg, .jpeg, .png"}
                isEditable={isEditable}
                isImage={true}
              >
                {fileList.length === 0 && !isEditable ? (
                  <p className="ant-upload-drag-icon">
                    <PortraitIcon />
                  </p>
                ) : fileList.length === 0 && isEditable ? (
                  "+ " + t("Upload Image")
                ) : null}
              </StyledUpload>
            </ImgCrop>
          )}
        </ImageInputColumn>
        {isEditable ? (
          <ImageInputColumn isLargeVideoStyle={isLargeVideoStyle}>
            <ImageInpupButton
              onClick={handleChangeBtn}
              style={{ color: "#5F5DF0", fontWeight: 700 }}
              icon={<PencilIcon />}
            >
              {type === "video" ? t("Change Video") : t("Change Picture")}
            </ImageInpupButton>
            <ImageInpupButton
              onClick={() => handleRemove(null)}
              style={{ color: "#313131", fontWeight: 400 }}
              icon={<CrossCircleIcon />}
            >
              {type === "video" ? t("Remove Video") : t("Remove Picture")}
            </ImageInpupButton>
          </ImageInputColumn>
        ) : null}
      </ImageInputWrapper>
    </StyledVerticalSpace>
  );
};
